import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { LogOut, ChevronDown, CreditCard, User, LifeBuoy } from 'lucide-react';
import { logoutUser } from '../../store/slices/authSlice';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuGroup,
} from "../ui/dropdown-menu";

export default function UserMenu() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { activeWorkspace } = useSelector((state) => state.workspace);

  const handleLogout = async () => {
    await dispatch(logoutUser());
    navigate('/login');
  };

  const initials = user?.name
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase() || 'MW';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          className="flex items-center gap-2.5 pl-2 pr-1.5 h-10 rounded-xl hover:bg-accent/40 border border-transparent hover:border-border/50 transition-all focus-visible:ring-1 focus-visible:ring-primary/20"
        >
          <Avatar className="h-7 w-7 border border-border/50 shadow-sm">
            <AvatarImage src={user?.avatar} />
            <AvatarFallback className="bg-primary/5 text-primary text-[10px] font-black tracking-tight">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="hidden sm:flex flex-col items-start gap-0 text-left">
            <span className="text-[12px] font-bold text-foreground leading-none">{user?.name || 'User'}</span>
            <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-tighter opacity-70">
              {activeWorkspace?.role || 'Member'}
            </span>
          </div>
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground/50 ml-0.5" />
        </Button>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent align="end" className="w-56 bg-popover/95 backdrop-blur-xl border border-border shadow-2xl rounded-2xl p-1.5 animate-in fade-in zoom-in duration-200">
        <div className="px-2.5 py-2 mb-1.5 border-b border-border/50">
          <p className="text-sm font-bold text-popover-foreground">{user?.name || 'User'}</p>
          <p className="text-[11px] text-muted-foreground font-medium truncate opacity-70">{user?.email || ''}</p>
        </div>
        
        <DropdownMenuGroup>
          <DropdownMenuItem onClick={() => navigate('/settings')} className="cursor-pointer text-[13px] font-medium rounded-xl h-9 gap-2">
            <User className="h-4 w-4 opacity-50" />
            <span>Profile Settings</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate('/plan')} className="cursor-pointer text-[13px] font-medium rounded-xl h-9 gap-2">
            <CreditCard className="h-4 w-4 opacity-50" />
            <span>Plan & Billing</span>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        
        <DropdownMenuSeparator className="bg-border/50" />
        
        <DropdownMenuGroup>
            <DropdownMenuItem onClick={() => navigate('/documentation')} className="cursor-pointer text-[13px] font-medium rounded-xl h-9 gap-2 text-muted-foreground">
                <LifeBuoy className="h-4 w-4 opacity-50" />
                <span>Help & Support</span>
            </DropdownMenuItem>
        </DropdownMenuGroup>

        <DropdownMenuSeparator className="bg-border/50" />
        
        <DropdownMenuItem 
          onClick={handleLogout} 
          className="text-destructive focus:text-destructive focus:bg-destructive/10 cursor-pointer text-[13px] font-bold rounded-xl h-9 gap-2"
        >
          <LogOut className="h-4 w-4" />
          <span>Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
